import { useCallback, useEffect, useRef } from 'react'

import { TOUCH_DRAG_DELAY_MS } from './touchDrag.constants'
import { useTouchMovement } from './useTouchMovement'

type TouchStartPosition = { x: number; y: number }

type UseTouchDragInitiationOptions = {
  onDragStart: (touchPosition: { x: number; y: number }) => void
  enabled?: boolean
}

export const useTouchDragInitiation = ({
  onDragStart,
  enabled = true
}: UseTouchDragInitiationOptions) => {
  const touchTimeoutRef = useRef<number | null>(null)
  const touchStartPosRef = useRef<TouchStartPosition | null>(null)
  const enabledRef = useRef(enabled)

  useEffect(() => {
    enabledRef.current = enabled
  }, [enabled])

  const clearTouchTimeout = useCallback(() => {
    if (touchTimeoutRef.current !== null) {
      window.clearTimeout(touchTimeoutRef.current)
      touchTimeoutRef.current = null
    }
  }, [])

  const {
    handleTouchStart: baseHandleTouchStart,
    handleTouchMove: baseHandleTouchMove,
    handleTouchEnd: baseHandleTouchEnd,
    reset
  } = useTouchMovement({
    onMovementExceedsTolerance: clearTouchTimeout,
    enabled
  })

  const handleTouchStart = useCallback(
    (event: React.TouchEvent) => {
      baseHandleTouchStart(event)
      if (!enabledRef.current) return

      const touch = event.touches[0]
      if (!touch) return

      const touchPos = { x: touch.clientX, y: touch.clientY }
      touchStartPosRef.current = touchPos

      touchTimeoutRef.current = window.setTimeout(() => {
        if (enabledRef.current && touchStartPosRef.current) {
          onDragStart(touchStartPosRef.current)
          clearTouchTimeout()
          reset()
        } else {
          clearTouchTimeout()
        }
      }, TOUCH_DRAG_DELAY_MS)
    },
    [baseHandleTouchStart, onDragStart, clearTouchTimeout, reset]
  )

  const handleTouchMove = useCallback(
    (event: React.TouchEvent) => {
      baseHandleTouchMove(event)
    },
    [baseHandleTouchMove]
  )

  const handleTouchEnd = useCallback(() => {
    baseHandleTouchEnd()
    clearTouchTimeout()
    touchStartPosRef.current = null
  }, [baseHandleTouchEnd, clearTouchTimeout])

  return {
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    clearTouchTimeout,
    touchStartPosition: touchStartPosRef.current
  }
}
